import {useState} from "react";
import {useMessages} from "i18n";
import {PolicyDialog, type PolicyKind} from "legal/PolicyDialog";
import {FREE_PLAN_ANCHOR_ID} from "pricing/pricingComparison";
import {PRICING_ROUTE} from "utils/HistoryUtils";
import {DocumentLink, Line, PlansLink} from "./SignUpTermsLine.styles";

/**
 * The sign-up Terms line (kitecorp/transferify-ui#3467): by creating a free account the person accepts the
 * Terms, including the 14-day right of withdrawal, and is pointed at the Privacy Policy.
 *
 * A free account is a consumer contract with its own pre-contractual information duties, because the
 * sign-up email is also used for product offers (the legal opinion on kitecorp/transferify-ui#2971).
 * Before this line the Terms first appeared at the first-send "I agree" gate, after the account already
 * existed. Where it stands is `login/loginViews.ts`'s: the send flow's email-code step and the `/login`
 * finish card draw it above their first account-creating control, the submit (`termsLineAbove`,
 * kitecorp/transferify-ui#3482). The login modal draws it on no view, the owner's decision of 2026-10-19
 * (kitecorp/transferify-ui#3674): the person accepts the Terms at the "Before you send" gate.
 *
 * Both document names open the CURRENT legal set through {@link PolicyDialog}, the dialog that gate
 * opens, so what a person accepts here is what the site publishes.
 *
 * The last sentence links the pricing page of the locale being read, opened at the plan comparison's Free
 * column, which carries the free plan's sizes (the legal opinion on kitecorp/transferify-ui#3483, section 4):
 * the page leads with the paid plans, and on a phone that column is off screen. It opens in a NEW tab,
 * as the advertising booking form's Terms link does: every surface holding this line has work in it that
 * leaving in place would lose (the finish card's token is read once and stripped from the address, and
 * the send flow holds the staged files), and the line exists to be read BEFORE that work is finished.
 *
 * <b>Why the dialog is mounted here, beside the link.</b> React bubbles a portal's events through the
 * tree it was declared in, so a click in the policy dialog travels up to whatever holds this line. The send
 * flow's code step and the finish card have no ancestor that acts on a click; a surface that does (the
 * login modal's centring layer closes on one) has to stop the click before it gets there.
 * `e2e/signup-terms-line.spec.ts` closes the dialog on every surface and checks the surface is still there
 * on the same step. MUI stacks the dialog above everything open because it opened last, and hands focus
 * back to the link on close.
 */
export function SignUpTermsLine({id}: {id: string}) {
	const {login, signUpTermsNotice} = useMessages();
	const [policy, setPolicy] = useState<PolicyKind | null>(null);
	const documentLink = (kind: PolicyKind, label: string) => (
		<DocumentLink type="button" onClick={() => setPolicy(kind)}>{label}</DocumentLink>
	);
	return (
		<>
			<Line id={id}>
				{signUpTermsNotice(documentLink("terms", login.signUpTerms.terms),
					documentLink("privacy", login.signUpTerms.privacy),
					<PlansLink to={`${PRICING_ROUTE}#${FREE_PLAN_ANCHOR_ID}`} target="_blank" rel="noopener">{login.signUpTerms.freePlan}</PlansLink>)}
			</Line>
			{policy !== null && <PolicyDialog kind={policy} open onClose={() => setPolicy(null)}/>}
		</>
	);
}
